import { components } from "@/api-schema";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { createConflictOverviewPath } from "./_helpers/create-conflict-overview-path";
import { groupConflictsByKey } from "./_helpers/group-conflicts-by-key";

type MergeConflict = components["schemas"]["MergeConflictResponse"];

interface MergeConflictListProps {
  conflicts: MergeConflict[];
  onRecheck: () => void;
  isRechecking: boolean;
}

export const MergeConflictList = ({ conflicts, onRecheck, isRechecking }: MergeConflictListProps) => {
  const { t } = useTranslation("masterdata");

  if (conflicts.length === 0) {
    return null;
  }

  const groups = [...groupConflictsByKey(conflicts).entries()];

  return (
    <div className="border-border bg-muted/20 mt-8 space-y-2 rounded-md border px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <AlertTriangle className="text-error h-4 w-4 shrink-0" />
        <p className="text-error min-w-0 flex-1 text-sm font-semibold wrap-break-word">
          {t("ConflictsMustBeResolvedBeforeMerging")}
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            size={ButtonSize.Sm}
            className="h-8 w-8 p-0"
            onClick={onRecheck}
            disabled={isRechecking}
            tooltip={t("RecheckConflicts")}
            aria-label={t("RecheckConflicts")}
          >
            {isRechecking ? (
              <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            size={ButtonSize.Sm}
            className="h-8 w-8 p-0"
            onClick={() => window.open(window.location.origin, "_blank", "noopener,noreferrer")}
            tooltip={t("OpenInNewTab")}
            aria-label={t("OpenInNewTab")}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {groups.map(([key, groupConflicts]) => {
          const relationLabel = t(`MergeRelation-${key}`, { defaultValue: key });
          const conflictHint = t(`MergeConflictHint-${groupConflicts[0].type}`, { defaultValue: "" });

          return (
            <div key={key} className="border-border space-y-2 border-t pt-4 first:border-t-0 first:pt-0">
              <div className="space-y-1">
                <div className="flex min-w-0 items-baseline gap-2">
                  <h3 className="min-w-0 text-base font-semibold wrap-break-word">{relationLabel}</h3>
                  {groupConflicts.length > 1 && (
                    <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{groupConflicts.length}</span>
                  )}
                </div>
                {conflictHint && <p className="text-muted-foreground text-xs">{conflictHint}</p>}
              </div>

              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_2rem] items-center gap-x-2 gap-y-1">
                {groupConflicts.map((conflict, conflictIndex) => {
                  const conflictOverviewPath = createConflictOverviewPath(
                    conflict.key,
                    conflict.records.map((record) => record.uuid),
                  );

                  return (
                    <div
                      key={JSON.stringify([conflict.type, conflict.key, conflict.records.map((record) => record.uuid)])}
                      className="contents"
                    >
                      <div className="min-w-0 py-1 text-sm">
                        {conflict.records.length === 0 ? (
                          <span className="text-muted-foreground">{t("MergeRelation-unknown")}</span>
                        ) : (
                          <span className="block min-w-0 wrap-anywhere">
                            {conflict.records.map((record, recordIndex) => (
                              <span key={record.uuid}>
                                {recordIndex > 0 && <span className="text-muted-foreground"> ↔ </span>}
                                {record.label}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                      <div className="flex h-8 items-center justify-center">
                        {conflictOverviewPath && (
                          <Button
                            asChild
                            variant={ButtonVariant.Ghost}
                            colorVariant={ButtonColorVariant.Secondary}
                            size={ButtonSize.Sm}
                            className="h-8 w-8 p-0"
                          >
                            <a
                              href={conflictOverviewPath}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t("OpenConflictOverview", { relation: relationLabel })}
                              aria-label={t("OpenConflictOverview", { relation: relationLabel })}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </Button>
                        )}
                      </div>
                      {conflictIndex < groupConflicts.length - 1 && <Separator className="bg-border col-span-full" />}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
